import { useEffect, useRef, useState } from "react";

export type NodeNavItem = {
  id: string;
  /** Human label (node name, falling back to its type label) — never a UUID. */
  label: string;
  /** Node-type display label (e.g. "Transform"), shown as secondary text. */
  typeLabel: string;
};

/**
 * Header node navigation for the operation-editor inspector flyout (ADR-0129
 * §3.3). Shows the selected node's name; clicking opens an anchored popover
 * (not a modal) listing the operation's nodes so the user can switch nodes
 * without closing a canvas-covering flyout.
 */
export function NodeNavDropdown({
  nodes,
  selectedId,
  onSelect,
}: {
  nodes: NodeNavItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = nodes.find((n) => n.id === selectedId);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="nodeNavDropdown" ref={wrapRef}>
      <button
        type="button"
        className="nodeNavDropdownButton"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={current?.label ?? "Select node"}
      >
        <span className="nodeNavDropdownLabel">{current?.label ?? "Select node"}</span>
        <span className="nodeNavDropdownChevron" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="nodeNavDropdownPopover" role="listbox" aria-label="Nodes">
          {nodes.map((n) => (
            <button
              key={n.id}
              type="button"
              role="option"
              aria-selected={n.id === selectedId}
              className={`nodeNavDropdownOption${n.id === selectedId ? " isActive" : ""}`}
              onClick={() => {
                onSelect(n.id);
                setOpen(false);
              }}
            >
              <span className="nodeNavDropdownOptionLabel">{n.label}</span>
              <span className="nodeNavDropdownOptionType muted">{n.typeLabel}</span>
            </button>
          ))}
          {nodes.length === 0 ? (
            <div className="nodeNavDropdownEmpty muted">No nodes</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
